import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { DEFAULT_INDICATOR_COLOR, DEFAULT_INDICATOR_FONT_SIZE_XS, DEFAULT_INDICATOR_LINE_WIDTH, MM_TO_INTERNAL_FACTOR } from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface IndicatorProps {
  width: number;
  length: number;
  rotation: number;
  sideC?: number;
}

const useLocalizedLabel = () => {
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();
  
  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "meter"), [locale, intl]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "foot"), [locale, intl]);

  const getLabel = (valInMM: number) => {
    const value = unitType === EUnitType.METRIC 
      ? valInMM / 1000 
      : convertTo(valInMM / 1000, "meters" as UnitsByMeasure<MeasureKind.Length>, "feet" as UnitsByMeasure<MeasureKind.Length>);
    
    return `${localizeNumber(value.toFixed(2), locale)} ${unitType === EUnitType.METRIC ? mLocalizedLabel : ftLocalizedLabel}`;
  };

  return getLabel;
};

const TriangleIndicator: React.FC<IndicatorProps> = ({ width, length, sideC = 0, rotation }) => {
  const getLabel = useLocalizedLabel();
  const OFFSET = 0.7; 
  const EXTENSION = 0.2;
  const TEXT_OFFSET = 0.5;

  const { v1, v2, v3 } = useMemo(() => {
    const a = width * MM_TO_INTERNAL_FACTOR;
    const b = length * MM_TO_INTERNAL_FACTOR;
    const c = sideC * MM_TO_INTERNAL_FACTOR;

    let cosGamma = (a * a + b * b - c * c) / (2 * a * b);
    cosGamma = Math.max(-1, Math.min(1, cosGamma));
    const sinGamma = Math.sqrt(1 - cosGamma * cosGamma);

    const p1 = new THREE.Vector3(0, 0, 0);
    const p2 = new THREE.Vector3(a, 0, 0);
    const p3 = new THREE.Vector3(b * cosGamma, b * sinGamma, 0);

    const minX = Math.min(p1.x, p2.x, p3.x);
    const maxX = Math.max(p1.x, p2.x, p3.x);
    const minY = Math.min(p1.y, p2.y, p3.y);
    const maxY = Math.max(p1.y, p2.y, p3.y);

    const centerOffset = new THREE.Vector3(-(minX + maxX) / 2, -(minY + maxY) / 2, 0);

    return {
      v1: p1.add(centerOffset),
      v2: p2.add(centerOffset),
      v3: p3.add(centerOffset),
    };
  }, [width, length, sideC]);

  const renderSide = (start: THREE.Vector3, end: THREE.Vector3, valInMM: number, key: string) => {
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const normal = new THREE.Vector3(dir.y, -dir.x, 0);

    const shift = normal.clone().multiplyScalar(OFFSET);
    const lineStart = start.clone().add(shift);
    const lineEnd = end.clone().add(shift);
    const mid = new THREE.Vector3().addVectors(lineStart, lineEnd).multiplyScalar(0.5);
    
    const textPos = mid.clone().add(normal.clone().multiplyScalar(TEXT_OFFSET));
    const tickVector = normal.clone().multiplyScalar(EXTENSION);
    
    return (
      <group key={key}>
        <Line points={[lineStart, lineEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
        
        <Line points={[lineStart, lineStart.clone().sub(tickVector)]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
        <Line points={[lineEnd, lineEnd.clone().sub(tickVector)]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
        
        <group position={textPos} rotation={[0, 0, rotation]}>
            <RoundedBoxWithText
              position={new THREE.Vector3(0, 0, 0)}
              fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
              textColor="black"
              text={getLabel(valInMM)}
            />
        </group>
      </group>
    );
  };

  return (
    <group>
      {/* FIX: v1->v2 is the base 'a' (width), so we pass width */}
      {renderSide(v1, v2, width, 'sideA')} 
      
      {/* v2->v3 is side 'c' */}
      {renderSide(v2, v3, sideC, 'sideC')} 
      
      {/* FIX: v3->v1 is side 'b' (length), so we pass length */}
      {renderSide(v3, v1, length, 'sideB')} 
    </group>
  );
};

const RectangleIndicator: React.FC<IndicatorProps> = ({ width, length, rotation }) => {
  const getLabel = useLocalizedLabel();
  const OFFSET = 0.7;
  const EXTENSION = 0.2;

  const { lengthLines, widthLines, lengthTextPos, widthTextPos } = useMemo(() => {
    const halfLength = (length / 2) * MM_TO_INTERNAL_FACTOR;
    const halfWidth = (width / 2) * MM_TO_INTERNAL_FACTOR;

    const lStart = new THREE.Vector3(-halfLength, -halfWidth - OFFSET, 0);
    const lEnd = new THREE.Vector3(halfLength, -halfWidth - OFFSET, 0);
    const lText = new THREE.Vector3(0, -halfWidth - OFFSET * 2, 0);
    const lExt1 = lStart.clone().add(new THREE.Vector3(0, EXTENSION, 0));
    const lExt2 = lEnd.clone().add(new THREE.Vector3(0, EXTENSION, 0));

    const wStart = new THREE.Vector3(halfLength + OFFSET, -halfWidth, 0);
    const wEnd = new THREE.Vector3(halfLength + OFFSET, halfWidth, 0);
    const wText = new THREE.Vector3(halfLength + OFFSET * 2, 0, 0);
    const wExt1 = wStart.clone().add(new THREE.Vector3(-EXTENSION, 0, 0));
    const wExt2 = wEnd.clone().add(new THREE.Vector3(-EXTENSION, 0, 0));

    return {
      lengthLines: { start: lStart, end: lEnd, ext1: lExt1, ext2: lExt2 },
      widthLines: { start: wStart, end: wEnd, ext1: wExt1, ext2: wExt2 },
      lengthTextPos: lText,
      widthTextPos: wText
    };
  }, [length, width]);

  return (
    <group>
      <Line points={[lengthLines.start, lengthLines.end]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[lengthLines.start, lengthLines.ext1]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[lengthLines.end, lengthLines.ext2]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <group position={lengthTextPos} rotation={[0, 0, rotation]}>
        <RoundedBoxWithText
          position={new THREE.Vector3(0, 0, 0)}
          fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
          textColor="black"
          text={getLabel(length)}
        />
      </group>

      <Line points={[widthLines.start, widthLines.end]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[widthLines.start, widthLines.ext1]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[widthLines.end, widthLines.ext2]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <group position={widthTextPos} rotation={[0, 0, rotation]}>
        <RoundedBoxWithText
          position={new THREE.Vector3(0, 0, 0)}
          fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
          textColor="black"
          text={getLabel(width)}
        />
      </group>
    </group>
  );
};

const ElementDimensionsIndicator: React.FC<IndicatorProps> = (props) => {
  if (props.sideC && props.sideC > 0) {
    return <TriangleIndicator {...props} />;
  }
  return <RectangleIndicator {...props} />;
};

export default ElementDimensionsIndicator;